import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Calendar as CalIcon, CreditCard, Wallet, Send, Loader2, CheckCircle2, ArrowRight, Users, Copy, Check, Plus, Trash2, Tag } from "lucide-react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { PayByCard } from "@/components/PayByCard";
import { TimeSelect } from "@/components/TimeSelect";
import api, { formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SERVICE_GROUPS = [
  { category: "Airport Transfer", items: [
    { value: "private_car", label: "Private Airport Car Service" },
    { value: "private_group_shuttle", label: "Airport Car Shuttle — Group" },
    { value: "shared_shuttle", label: "Airport Car Shuttle — Shared" },
  ] },
  { category: "Tours", items: [
    { value: "tour", label: "Tour — price agreed by phone" },
  ] },
];
const SERVICES = SERVICE_GROUPS.flatMap((g) => g.items);
const PRIVATE = {
  halifax_downtown: { label: "Halifax Downtown & surrounding areas", price3: 60, price6: 70 },
  dartmouth: { label: "Downtown Dartmouth area", price3: 60, price6: 70 },
  lower_sackville: { label: "Lower Sackville area", price3: 55, price6: 62 },
  middle_sackville: { label: "Middle Sackville", price3: 65, price6: 75 },
  upper_sackville: { label: "Upper Sackville", price3: 70, price6: 80 },
  spryfield: { label: "Spryfield area", price3: 65, price6: 75 },
};
const SHARED_SHUTTLE_PER_PERSON = 45;
const GROUP_SHUTTLE = { 1: 45, 2: 30, 3: 25, 4: 20, 5: 17.5, 6: 15 };
const HOURLY = { special_event: { rate3: 65, rate6: 75, min: 2 }, wedding: { rate3: 65, rate6: 75, min: 3 }, corporate_account: { rate3: 65, rate6: 75, min: 2 }, valley_fundy_tour: { rate3: 65, rate6: 75, min: 3 } };
const LONG_TRIP = {
  deep_cove_chester: { label: "Deep Cove Road, Chester, NS", price: 140 },
  crystal_beach_ambrose: { label: "Crystal Beach (Old Ambrose Rd), NS", price: 65 },
  fredericton: { label: "Fredericton, NB", price: 650 },
  cape_north: { label: "Cape North, NS", price: 600 },
  cape_breton_university: { label: "Cape Breton University (Sydney), NS", price: 550 },
  saint_john: { label: "Saint John, NB", price: 550 },
  sydney: { label: "Sydney, NS", price: 550 },
  adventure_bay_tiverton: { label: "Adventure Bay Whale Watch Co. (Tiverton), NS", price: 500 },
  inverness: { label: "Inverness, NS", price: 500 },
  charlottetown_airport: { label: "Charlottetown, PEI", price: 460 },
  yarmouth: { label: "Yarmouth, NS", price: 440 },
  digby: { label: "Digby, NS", price: 425 },
  baddeck: { label: "Baddeck, NS", price: 420 },
  canso: { label: "Canso, NS", price: 420 },
  moncton: { label: "Moncton, NB", price: 400 },
  ste_anne_university: { label: "Universite Sainte-Anne (Church Point), NS", price: 400 },
  churchill_point: { label: "Churchill Point, NS", price: 380 },
  barrington: { label: "Barrington, NS", price: 350 },
  canso_causeway: { label: "Canso Causeway, NS", price: 350 },
  clarks_harbour: { label: "Clark's Harbour, NS", price: 350 },
  country_harbour: { label: "Country Harbour, NS", price: 350 },
  port_hawkesbury: { label: "Port Hawkesbury, NS", price: 350 },
  deep_brook: { label: "Deep Brook, NS", price: 340 },
  shelburne: { label: "Shelburne, NS", price: 325 },
  bridgetown: { label: "Bridgetown, NS", price: 300 },
  cornwallis: { label: "Cornwallis, NS", price: 300 },
  stfx_university: { label: "StFX University (Antigonish), NS", price: 290 },
  antigonish: { label: "Antigonish, NS", price: 275 },
  amherst: { label: "Amherst, NS", price: 250 },
  liverpool: { label: "Liverpool, NS", price: 250 },
  greenwood: { label: "Greenwood, NS", price: 225 },
  fox_harbour: { label: "Fox Harbour, NS", price: 210 },
  pictou: { label: "Pictou, NS", price: 210 },
  bridgewater: { label: "Bridgewater, NS", price: 200 },
  canning: { label: "Canning, NS", price: 200 },
  new_glasgow: { label: "New Glasgow, NS", price: 200 },
  port_williams: { label: "Port Williams, NS", price: 180 },
  berwick: { label: "Berwick, NS", price: 175 },
  kentville: { label: "Kentville, NS", price: 175 },
  new_minas: { label: "New Minas, NS", price: 170 },
  lunenburg: { label: "Lunenburg, NS", price: 165 },
  coldbrook: { label: "Coldbrook, NS", price: 160 },
  wolfville: { label: "Wolfville, NS", price: 160 },
  acadia_university: { label: "Acadia University (Wolfville), NS", price: 155 },
  mahone_bay: { label: "Mahone Bay, NS", price: 145 },
  hantsport: { label: "Hantsport, NS", price: 140 },
  chester: { label: "Chester, NS", price: 130 },
  debert: { label: "Debert, NS", price: 130 },
  blandford: { label: "Blandford, NS", price: 125 },
  ferrychester: { label: "Ferrychester, NS", price: 125 },
  truro: { label: "Truro (from Airport), NS", price: 125 },
  bible_hill: { label: "Bible Hill, NS", price: 120 },
  chezzetcook: { label: "Chezzetcook, NS", price: 105 },
  dover_east: { label: "Dover (East), NS", price: 105 },
  hubbards: { label: "Hubbards, NS", price: 105 },
  brookfield: { label: "Brookfield, NS", price: 100 },
  east_uniacke: { label: "East Uniacke, NS", price: 85 },
  hatchet_lake: { label: "Hatchet Lake, NS", price: 75 },
  herring_cove: { label: "Herring Cove, NS", price: 75 },
};
const LOCAL = {
  bayswater: { label: "Bayswater", price: 105 },
  boutiliers_point: { label: "Boutiliers Point", price: 94 },
  black_point: { label: "Black Point", price: 90 },
  dover: { label: "Dover", price: 90 },
  bayside: { label: "Bayside", price: 80 },
  brookside: { label: "Brookside", price: 80 },
  beaver_bank: { label: "Beaver Bank", price: 70 },
  oakwood_road: { label: "Oakwood Road", price: 70 },
  williams_lake: { label: "Williams Lake", price: 70 },
  exhibition_park: { label: "Exhibition Park", price: 65 },
  shearwater: { label: "Shearwater", price: 65 },
  bedford_south: { label: "Bedford South", price: 60 },
  cole_harbour: { label: "Cole Harbour", price: 60 },
  elderbank: { label: "Elderbank", price: 60 },
  frost_hill: { label: "Frost Hill", price: 60 },
  kingswood: { label: "Kingswood", price: 60 },
  rogers_lake: { label: "Rogers Lake", price: 60 },
  woodside: { label: "Woodside", price: 60 },
  elmsdale: { label: "Elmsdale", price: 55 },
  hammonds_plains_clearwater: { label: "Hammonds Plains Rd (Clearwater)", price: 55 },
  hammonds_plains_road: { label: "Hammonds Plains Road", price: 55 },
  enfield: { label: "Enfield", price: 50 },
  dutch_settlement: { label: "Dutch Settlement", price: 45 },
  cow_bay: { label: "Cow Bay", price: 80 },
  harrietsfield: { label: "Harrietsfield", price: 80 },
  eastern_passage: { label: "Eastern Passage", price: 75 },
  beechville: { label: "Beechville", price: 70 },
  hubley: { label: "Hubley", price: 70 },
  bedford: { label: "Bedford", price: 65 },
  goffs: { label: "Goffs", price: 42 },
};
const TOUR_RATE_3 = 65;
const TOUR_RATE_6 = 75;
const TOUR_PACKAGES = {
  peggys_city: { label: "Peggy's Cove & City Tour", hours: 3, stops: ["Peggy's Cove", "Halifax City highlights"] },
  titanic_peggys: { label: "Titanic Graveyard & Peggy's Cove", hours: 4, stops: ["Titanic Graveyard (Fairview Lawn)", "Peggy's Cove"] },
  south_shore: { label: "South Shore Explorer", hours: 5, stops: ["Peggy's Cove", "Mahone Bay", "Lunenburg"] },
  city_highlights_6h: { label: "Halifax Highlights & Peggy's Cove", hours: 6, stops: ["Halifax City", "Citadel Hill", "Public Gardens", "Point Pleasant Park", "Titanic Graveyard", "Peggy's Cove"] },
  grand_tour: { label: "Grand Nova Scotia Tour", hours: 7, stops: ["Peggy's Cove", "Mahone Bay", "Lunenburg", "Titanic Graveyard", "Citadel Hill"] },
};
const ETRANSFER_EMAIL = "disassadula@gmail.com";
const EXTRAS = [
  { value: "booster", label: "Booster seat", price: 10 },
  { value: "car_seat", label: "Car seat", price: 10 },
  { value: "extra_large", label: "Extra large size", price: 10 },
];

const ADDON_FEES = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

const inputCls = "w-full rounded-sm bg-input border border-gold/20 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold transition-shadow duration-200";
const labelCls = "block text-sm font-medium text-slate-300 mb-1.5";

function computeQuote(service, option, passengers, hours, extras = [], vehicles = 1, roundTrip = false, addonFee = 0, customFare = 0) {
  if (service === "custom_fare") {
    const amt = Math.max(+customFare || 0, 0);
    if (!amt) return null;
    return { amount: +amt.toFixed(2), desc: "Custom trip — agreed fare" };
  }
  const base = computeBase(service, option, passengers, hours);
  const cf0 = Math.max(+customFare || 0, 0);
  if (!base) {
    if (cf0) return { amount: +cf0.toFixed(2), desc: "Agreed fare" };
    return base;
  }
  const v = Math.max(+vehicles || 1, 1);
  let baseTotal = +(base.amount * v).toFixed(2);
  let desc = base.desc + (v > 1 ? ` × ${v} vehicles` : "");
  const rtEligible = roundTrip && !HOURLY[service] && !["tours", "city_tour"].includes(service);
  if (rtEligible) {
    baseTotal = +(baseTotal * 2).toFixed(2);
    desc += " (round trip)";
  }
  const picked = EXTRAS.filter((e) => extras.includes(e.value));
  const extraTotal = picked.reduce((s, e) => s + e.price, 0);
  if (picked.length) desc += ` + ${picked.map((e) => e.label).join(", ")}`;
  const fee = Math.max(+addonFee || 0, 0);
  if (fee) desc += ` + Additional fee ($${fee})`;
  const cf = Math.max(+customFare || 0, 0);
  if (cf) return { amount: +cf.toFixed(2), desc: `${desc} · Fixed rate` };
  return { amount: +(baseTotal + extraTotal + fee).toFixed(2), desc };
}

function computeBase(service, option, passengers, hours) {
  if (service === "private_car" && option && PRIVATE[option]) {
    const d = PRIVATE[option];
    const isBig = passengers && passengers > 3;
    return { amount: isBig ? d.price6 : d.price3, desc: `Private Airport Car — To/From ${d.label} (${isBig ? "4–6" : "up to 3"} passengers)` };
  }
  if ((service === "city_tour" || service === "tours") && option && TOUR_PACKAGES[option]) {
    const p = TOUR_PACKAGES[option];
    const big = passengers && passengers > 3;
    const rate = big ? TOUR_RATE_6 : TOUR_RATE_3;
    return { amount: p.hours * rate, desc: `Tour — ${p.label} (${p.hours} hr, ${big ? "4–6" : "up to 3"} passengers @ $${rate}/hr)` };
  }
  if (service === "long_trip" && option && LONG_TRIP[option]) {
    const d = LONG_TRIP[option];
    return { amount: d.price, desc: `Long Distance — To/From ${d.label}` };
  }
  if (service === "local_area" && option && LOCAL[option]) {
    const d = LOCAL[option];
    return { amount: d.price, desc: `Nearby & Local Area — To/From ${d.label}` };
  }
  if (service === "shared_shuttle" && passengers) {
    const per = SHARED_SHUTTLE_PER_PERSON;
    return { amount: +(per * passengers).toFixed(2), desc: `Shared Shuttle — ${passengers} pax @ $${per.toFixed(2)} each` };
  }
  if (service === "private_group_shuttle" && passengers) {
    const per = GROUP_SHUTTLE[passengers];
    return { amount: +(per * passengers).toFixed(2), desc: `Private Group Airport Shuttle — ${passengers} passenger(s) @ $${per.toFixed(2)} each, same pickup` };
  }
  if (service === "golf_club") {
    return { amount: 85, desc: "Golf Club Ride — per van (flat rate)" };
  }
  if (HOURLY[service] && hours) {
    const h = HOURLY[service];
    if (h.rate6 !== undefined) {
      const big = passengers && passengers > 3;
      const rate = big ? h.rate6 : h.rate3;
      return { amount: +(rate * hours).toFixed(2), desc: `${hours} hour(s) @ $${rate}/hr (${big ? "4–6" : "up to 3"} passengers)` };
    }
    return { amount: +(h.rate * hours).toFixed(2), desc: `${hours} hour(s) @ $${h.rate}/hr` };
  }
  return null;
}

export default function Booking() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [service, setService] = useState(() => {
    const s = params.get("service");
    return s && SERVICES.some((x) => x.value === s) ? s : "";
  });
  const [option, setOption] = useState(() => params.get("option") || "");
  const [passengers, setPassengers] = useState(() => params.get("passengers") || "");
  const [hours, setHours] = useState(() => params.get("hours") || "");
  const [vehicles, setVehicles] = useState("1");
  const [date, setDate] = useState();
  const [timeH, setTimeH] = useState("");
  const [timeMin, setTimeMin] = useState("");
  const [timeAP, setTimeAP] = useState("");
  const time = timeH && timeMin && timeAP ? `${timeH}:${timeMin} ${timeAP}` : "";
  const [dropH, setDropH] = useState("");
  const [dropMin, setDropMin] = useState("");
  const [dropAP, setDropAP] = useState("");
  const dropoffTime = dropH && dropMin && dropAP ? `${dropH}:${dropMin} ${dropAP}` : "";
  const [direction, setDirection] = useState("");
  const [roundTrip, setRoundTrip] = useState(false);
  const [returnDate, setReturnDate] = useState();
  const [rtH, setRtH] = useState("");
  const [rtMin, setRtMin] = useState("");
  const [rtAP, setRtAP] = useState("");
  const returnTime = rtH && rtMin && rtAP ? `${rtH}:${rtMin} ${rtAP}` : "";
  const [stops, setStops] = useState([]);
  const [addonFee, setAddonFee] = useState("");
  const [customFare, setCustomFare] = useState("");
  const [cruiseShip, setCruiseShip] = useState("");
  const [tourDuration, setTourDuration] = useState("");
  const [cruiseList, setCruiseList] = useState([]);
  useEffect(() => {
    api.get("/cruise-ships").then(({ data }) => setCruiseList(data)).catch(() => {});
  }, []);
  useEffect(() => {
    const rid = params.get("rebook");
    if (!rid) return;
    api.get(`/bookings/${rid}`).then(({ data: b }) => {
      setForm((f) => ({ ...f, name: b.name || "", email: b.email || "", phone: b.phone || "", pickup: b.pickup || "", dropoff: b.dropoff || "" }));
      if (b.service_type && SERVICES.some((x) => x.value === b.service_type)) setService(b.service_type);
      if (b.direction) setDirection(b.direction);
      const code = params.get("promo");
      if (code && b.email) {
        setPromo(code);
        api.post("/promo/validate", { code, email: b.email }).then(({ data }) => {
          if (data.valid) { setPromoInfo(data); toast.success(`Welcome back${b.name ? ", " + b.name : ""}! Your ${data.percent}% returning-customer discount is applied — choose your service to see it.`, { id: "rebook" }); }
          else toast.success(`Welcome back${b.name ? ", " + b.name : ""}! Your details are filled in.`, { id: "rebook" });
        }).catch(() => {});
        return;
      }
      toast.success(`Welcome back${b.name ? ", " + b.name : ""}! Your details are filled in — just pick a date and time.`, { id: "rebook" });
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [luggage, setLuggage] = useState("");
  const [checkingBags, setCheckingBags] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [extras, setExtras] = useState([]);
  const [showTerms, setShowTerms] = useState(false);
  const [payment, setPayment] = useState("pay_later");
  const [plan, setPlan] = useState("full");
  const [tipPct, setTipPct] = useState(0);
  const [customTip, setCustomTip] = useState("");
  const [inviteEmails, setInviteEmails] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [cart, setCart] = useState([]);
  const [promo, setPromo] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", pickup: "", dropoff: "", notes: "", flight_number: "" });
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [splitCount, setSplitCount] = useState("");
  const [splitInfo, setSplitInfo] = useState(null);
  const [splitLoading, setSplitLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const isHourly = !!HOURLY[service];
  const isCustom = service === "custom_fare";
  const rtEligible = !!service && !isHourly && !isCustom && !["tours", "city_tour", "tour"].includes(service);
  const quote = computeQuote(service, option, passengers ? +passengers : null, hours ? +hours : null, extras, +vehicles || 1, roundTrip && rtEligible, +addonFee || 0, +customFare || 0);
  const toggleExtra = (v) => setExtras((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);

  const cartTotal = cart.reduce((s, it) => s + it.amount, 0);
  const currentTotal = quote ? quote.amount : 0;
  const orderTotal = +(cartTotal + currentTotal).toFixed(2);
  const discount = promoInfo ? +(orderTotal * promoInfo.percent / 100).toFixed(2) : 0;
  const taxable = +(orderTotal - discount).toFixed(2);
  const HST_RATE = 0.15;
  const taxAmount = payment === "online" ? +(taxable * HST_RATE).toFixed(2) : 0;
  const total = +(taxable + taxAmount).toFixed(2);
  const deposit = +Math.min(Math.max(total * 0.2, 20), total).toFixed(2);
  const showPlan = payment !== "pay_later" && total > 0;
  const dueNow = payment === "pay_later" ? 0 : (plan === "deposit" ? deposit : total);
  const balanceAfter = +(total - dueNow).toFixed(2);
  const tipAmount = payment === "online" ? (customTip !== "" ? Math.max(+customTip || 0, 0) : +(total * tipPct / 100).toFixed(2)) : 0;
  const chargedNow = +(dueNow + tipAmount).toFixed(2);

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const splitUrl = confirmed ? `${window.location.origin}/split/${confirmed.id}` : "";

  const generateSplit = async () => {
    const n = parseInt(splitCount, 10);
    if (!n || n < 2 || n > 12) return toast.error("Choose between 2 and 12 people.");
    setSplitLoading(true);
    try {
      const { data } = await api.post(`/split/${confirmed.id}/enable`, { shares: n });
      setSplitInfo(data);
      toast.success("Split link created — share it with your group!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create split link.");
    } finally {
      setSplitLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(splitUrl);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const sendInvites = async () => {
    const emails = inviteEmails.split(/[,\s]+/).map((e) => e.trim()).filter(Boolean);
    if (!emails.length) return toast.error("Enter at least one email address.");
    setInviteLoading(true);
    try {
      const { data } = await api.post(`/split/${confirmed.id}/invite`, { emails });
      toast.success(`Split link sent to ${data.sent} of ${data.total} ${data.total === 1 ? "person" : "people"}.`);
      setInviteEmails("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send invites.");
    } finally {
      setInviteLoading(false);
    }
  };

  const OPTION_SERVICES = ["private_car", "city_tour", "tours", "long_trip", "local_area"];
  const PAX_SERVICES = ["shared_shuttle", "private_group_shuttle", "private_car", "valley_fundy_tour", "tours", "city_tour", "special_event", "wedding", "corporate_account"];

  const validateService = () => {
    if (!service) return "Please choose a service.";
    if (service === "custom_fare" && (!customFare || +customFare <= 0)) return "Please enter the agreed fare amount.";
    if (isHourly && hours && +hours < HOURLY[service].min) return `Minimum ${HOURLY[service].min} hours for this service.`;
    return null;
  };

  const buildItem = () => ({
    service_type: service,
    option: OPTION_SERVICES.includes(service) ? option : null,
    passengers: (PAX_SERVICES.includes(service) || service === "tour") ? (+passengers || null) : null,
    hours: isHourly ? +hours : null,
    vehicles: +vehicles || 1,
    extras,
    direction: direction || null,
    flight_number: form.flight_number || null,
    luggage: luggage || null,
    checking_bags: checkingBags || null,
    date: date ? format(date, "yyyy-MM-dd") : null, time: time || null,
    round_trip: roundTrip && rtEligible,
    return_datetime: (roundTrip && rtEligible && returnDate) ? `${format(returnDate, "yyyy-MM-dd")} ${returnTime}`.trim() : null,
    pickup: form.pickup || null, dropoff: form.dropoff || null, notes: form.notes || null,
    stops: stops.map((s) => s.trim()).filter(Boolean),
    addon_fee: +addonFee || 0,
    custom_fare: (+customFare || 0) || null,
    cruise_ship: service === "tour" ? (cruiseShip || null) : null,
    tour_duration: service === "tour" ? (tourDuration || null) : null,
    dropoff_time: service === "tour" ? (dropoffTime || null) : null,
    description: quote ? quote.desc : service, amount: quote ? quote.amount : 0,
  });

  const resetServiceFields = () => {
    setService(""); setOption(""); setPassengers(""); setHours(""); setVehicles("1");
    setExtras([]); setDirection(""); setLuggage(""); setCheckingBags(""); setDate(undefined); setTimeH(""); setTimeMin(""); setTimeAP("");
    setRoundTrip(false); setReturnDate(undefined); setRtH(""); setRtMin(""); setRtAP(""); setStops([]); setAddonFee(""); setCustomFare("");
    setCruiseShip(""); setTourDuration(""); setDropH(""); setDropMin(""); setDropAP("");
    setForm((f) => ({ ...f, pickup: "", dropoff: "", notes: "", flight_number: "" }));
  };

  const addToCart = () => {
    const err = validateService();
    if (err) return toast.error(err);
    setCart((prev) => [...prev, buildItem()]);
    resetServiceFields();
    toast.success("Service added to your order — add another or confirm below.");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeItem = (i) => setCart((prev) => prev.filter((_, idx) => idx !== i));

  const applyPromo = async () => {
    if (!promo.trim()) return;
    if (!form.email) return toast.error("Enter your email address first to apply a code.");
    setPromoLoading(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promo.trim(), email: form.email });
      if (data.valid) { setPromoInfo(data); toast.success(`Code applied — ${data.percent}% off!`); }
      else { setPromoInfo(null); toast.error("Invalid or expired code for this email."); }
    } catch { toast.error("Could not validate code."); }
    finally { setPromoLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const items = [...cart];
    if (service) {
      const err = validateService();
      if (err) return toast.error(err);
      items.push(buildItem());
    }
    if (items.length === 0) return toast.error("Please add at least one service to your order.");

    const contact = { name: form.name || undefined, email: form.email || undefined, phone: form.phone || undefined };
    const paymentFields = { payment_method: payment, payment_plan: payment === "pay_later" ? "full" : plan, promo_code: promoInfo ? promo.trim() : null };

    setLoading(true);
    try {
      let booking;
      if (items.length === 1) {
        const res = await api.post("/bookings", { ...items[0], ...contact, ...paymentFields });
        booking = res.data;
      } else {
        const res = await api.post("/orders", { items, ...contact, ...paymentFields });
        booking = res.data;
      }
      trackEvent("booking_confirmed", { currency: "CAD", value: booking.amount, service: items.length > 1 ? "group_order" : items[0].service_type, payment_method: payment });
      if (payment === "online") {
        const { data } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin, tip: tipAmount });
        window.location.href = data.checkout_url;
        return;
      }
      setConfirmed(booking);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Booking failed.");
    } finally {
      setLoading(false);
    }
  };

  if (confirmed) {
    const dep = confirmed.deposit_amount || 0;
    const bal = +(confirmed.amount - dep).toFixed(2);
    const isDep = confirmed.payment_plan === "deposit" && dep > 0 && dep < confirmed.amount;
    const isETransfer = confirmed.payment_method === "e_transfer";
    return (
      <section className="max-w-2xl mx-auto px-5 sm:px-8 py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="rounded-sm bg-navy-light border border-gold/30 p-10 text-center" data-testid="booking-confirmed">
          <CheckCircle2 className="h-16 w-16 text-gold mx-auto mb-5" />
          <h1 className="font-heading text-3xl font-bold text-white">Booking confirmed!</h1>
          <p className="text-slate-300 mt-3">Thank you, {confirmed.name}! We've got your booking and we'll be in touch shortly to confirm the details.</p>
          <div className="mt-8 text-left rounded-sm bg-navy border border-gold/20 p-6 space-y-2 text-sm">
            {confirmed.items ? (
              <div className="space-y-3" data-testid="order-items">
                {confirmed.items.map((it, i) => (
                  <div key={i} className="pb-3 border-b border-gold/10 last:border-0">
                    <div className="flex justify-between gap-3">
                      <span className="text-white font-medium">{i + 1}. {it.description}</span>
                      <span className="text-gold font-semibold whitespace-nowrap">${it.amount.toFixed(2)}</span>
                    </div>
                    <div className="text-slate-500 text-xs mt-1">{it.date} at {it.time} · {it.pickup}</div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <Row l="Service" v={confirmed.description} />
                {confirmed.direction && <Row l="Where to" v={confirmed.direction === "to_airport" ? "To the airport" : "From the airport"} />}
                {(confirmed.date || confirmed.time) && <Row l="Date & time" v={`${confirmed.date || "TBD"}${confirmed.time ? ` at ${confirmed.time}` : ""}`} />}
                {confirmed.pickup && <Row l="Pickup" v={confirmed.pickup} />}
                {confirmed.flight_number && <Row l="Flight" v={confirmed.flight_number} />}
                {confirmed.luggage && <Row l="Luggage" v={confirmed.luggage === "none" ? "No luggage" : confirmed.luggage === "4+" ? "4+ bags" : `${confirmed.luggage} bag(s)`} />}
                {confirmed.checking_bags && <Row l="Checking bags" v={confirmed.checking_bags === "yes" ? "Yes" : "No (carry-on)"} />}
                {confirmed.extras && confirmed.extras.length > 0 && <Row l="Add-ons" v={confirmed.extras.map((e) => (EXTRAS.find((x) => x.value === e) || {}).label || e).join(", ")} />}
              </>
            )}
            {confirmed.discount_amount > 0 && <Row l="Discount" v={`−$${confirmed.discount_amount.toFixed(2)} ${confirmed.currency}`} />}
            <Row l="Total fare" v={`$${confirmed.amount.toFixed(2)} ${confirmed.currency}`} gold />
            {isDep && <Row l={isETransfer ? "Deposit due now" : "Paid today"} v={`$${dep.toFixed(2)} ${confirmed.currency}`} />}
            {isDep && <Row l="Balance due on arrival" v={`$${bal.toFixed(2)} ${confirmed.currency}`} />}
            <Row l="Payment" v={isETransfer ? "Interac e-Transfer" : "Pay driver on arrival (cash or debit machine)"} />
            <Row l="Reference" v={confirmed.id.slice(0, 8).toUpperCase()} />
          </div>
          <a href={`/manage/${confirmed.id}`} data-testid="manage-booking-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-gold hover:text-gold-light transition-colors duration-200">
            Need to change something? Edit your booking →
          </a>
          {confirmed.payment_method === "e_transfer" && (
            <div className="mt-6 text-left rounded-sm bg-gold/10 border border-gold/40 p-6" data-testid="etransfer-instructions">
              <div className="flex items-center gap-2 mb-3">
                <Send className="h-5 w-5 text-gold" />
                <h3 className="font-heading text-lg font-bold text-white">Complete your Interac e-Transfer</h3>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">Please send your e-Transfer of <span className="text-gold font-semibold">${(isDep ? dep : confirmed.amount).toFixed(2)} {confirmed.currency}</span> to:</p>
              <p className="mt-2 text-lg font-semibold text-gold break-all" data-testid="etransfer-email">{ETRANSFER_EMAIL}</p>
              <p className="mt-3 text-xs text-slate-400 leading-relaxed">This account has <span className="text-slate-200">auto-deposit</span> enabled — no security question is needed. Please add your booking reference <span className="text-gold font-semibold">{confirmed.id.slice(0, 8).toUpperCase()}</span> in the message.{isDep ? ` The remaining $${bal.toFixed(2)} ${confirmed.currency} is due to your driver on arrival.` : ""} Your ride is confirmed once we receive the transfer.</p>
            </div>
          )}
          {(confirmed.balance_due ?? confirmed.amount) > 0.01 && (
            <div className="mt-6 text-left" data-testid="confirm-paycard">
              <PayByCard bookingId={confirmed.id} balance={confirmed.balance_due ?? confirmed.amount} currency={confirmed.currency} />
            </div>
          )}

          <div className="mt-6 text-left rounded-sm bg-navy border border-gold/20 p-6" data-testid="split-section">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-gold" />
              <h3 className="font-heading text-lg font-bold text-white">Split the bill with your group</h3>
            </div>
            <p className="text-sm text-slate-400 mb-4">Share a link so each passenger pays their equal share online.</p>
            {!splitInfo ? (
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className={labelCls}>How many people?</label>
                  <input type="number" min={2} max={12} value={splitCount} onChange={(e) => setSplitCount(e.target.value)} placeholder="e.g. 4" data-testid="split-count-input" className={`${inputCls} w-32`} />
                </div>
                <button type="button" onClick={generateSplit} disabled={splitLoading} data-testid="split-generate-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-semibold text-navy transition-colors duration-200 hover:bg-gold-light disabled:opacity-60">
                  {splitLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />} Create split link
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Each person pays</span>
                  <span className="font-semibold text-gold" data-testid="split-share-amount">${splitInfo.split.share_amount.toFixed(2)} × {splitInfo.split.shares}</span>
                </div>
                <div className="flex items-center gap-2">
                  <input readOnly value={splitUrl} data-testid="split-link" className={`${inputCls} text-xs`} />
                  <button type="button" onClick={copyLink} data-testid="split-copy-btn"
                    className="shrink-0 rounded-sm border border-gold/40 p-3 text-gold transition-colors duration-200 hover:bg-gold/10">
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <div className="flex justify-center bg-white rounded-sm p-4 w-fit mx-auto" data-testid="split-qr">
                  <QRCodeCanvas value={splitUrl} size={148} />
                </div>
                <p className="text-xs text-slate-500 text-center">Scan or share the link — passengers pay by card, or use the e-Transfer instructions on the page.</p>
                <div className="border-t border-gold/20 pt-4">
                  <label className={labelCls}>Email the link to your group <span className="text-slate-500">(optional)</span></label>
                  <div className="flex items-center gap-2">
                    <input value={inviteEmails} onChange={(e) => setInviteEmails(e.target.value)} placeholder="email1@x.com, email2@x.com" data-testid="split-invite-input" className={`${inputCls} text-xs`} />
                    <button type="button" onClick={sendInvites} disabled={inviteLoading} data-testid="split-invite-btn"
                      className="shrink-0 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-gold-light disabled:opacity-60">
                      {inviteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <button onClick={() => navigate("/")} className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 font-semibold text-navy transition-transform duration-200 hover:-translate-y-0.5 hover:bg-gold-light" data-testid="confirmed-home-btn">
            Back to home <ArrowRight className="h-4 w-4" />
          </button>
        </motion.div>
      </section>
    );
  }

  return (
    <div className="max-w-[1500px] mx-auto px-5 sm:px-10 pt-10 sm:pt-16 pb-16 sm:pb-24">
      <div className="mb-10">
        <span className="text-sm uppercase tracking-[0.2em] text-gold">Reserve</span>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold text-white tracking-tighter">Book your ride</h1>
        <p className="mt-4 text-slate-300 max-w-2xl">Choose your service, tell us where and when, and see your fare instantly. Booking for a group or event? Add several services and pay once.</p>
        <p className="mt-2 text-sm text-slate-400">Don't see your trip? <Link to="/quote" data-testid="booking-quote-link" className="text-gold underline underline-offset-2 hover:text-gold-light">Request a custom quote →</Link></p>
      </div>

      <form onSubmit={submit} className="grid gap-8 lg:grid-cols-3" data-testid="booking-form">
        <div className="lg:col-span-2 space-y-6">
          {/* Service */}
          <div className="rounded-sm bg-navy-light border border-gold/20 p-7">
            <h3 className="font-heading text-lg font-bold text-white mb-5">1. Choose your service</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelCls}>Service type</label>
                <Select value={service} onValueChange={(v) => { setService(v); setOption(""); setPassengers(""); setHours(""); }}>
                  <SelectTrigger data-testid="select-service" className="bg-input border-gold/20 text-white h-12"><SelectValue placeholder="Select a service" /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_GROUPS.map((g) => (
                      <SelectGroup key={g.category}>
                        <SelectLabel className="text-gold text-xs uppercase tracking-wider">{g.category}</SelectLabel>
                        {g.items.map((s) => (
                          <SelectItem key={s.value} value={s.value} data-testid={`service-opt-${s.value}`}>{s.label}</SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {service === "private_car" && (
                <>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Destination (to/from Halifax Airport) <span className="text-slate-500">(optional)</span></label>
                    <Select value={option} onValueChange={setOption}>
                      <SelectTrigger data-testid="select-option" className="bg-input border-gold/20 text-white h-12"><SelectValue placeholder="Select destination (optional)" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {Object.entries(PRIVATE).map(([k, d]) => (
                          <SelectItem key={k} value={k} data-testid={`private-car-opt-${k}`}>{d.label} — ${d.price3}/${d.price6}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Passengers</label>
                    <Select value={passengers} onValueChange={setPassengers}>
                      <SelectTrigger data-testid="select-private-pax" className="bg-input border-gold/20 text-white h-12"><SelectValue placeholder="Select passengers" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">Up to 3 passengers{option && PRIVATE[option] ? ` — $${PRIVATE[option].price3}` : ""}</SelectItem>
                        <SelectItem value="6">Up to 6 passengers{option && PRIVATE[option] ? ` — $${PRIVATE[option].price6}` : ""}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              {service === "tour" && (
                <>
                  <div>
                    <label className={labelCls}>Number of passengers <span className="text-slate-500">(optional)</span></label>
                    <input data-testid="input-tour-passengers" type="number" min="1" max="60" value={passengers} onChange={(e) => setPassengers(e.target.value)} placeholder="e.g. 4" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Time of tour / duration <span className="text-slate-500">(optional)</span></label>
                    <input data-testid="input-tour-duration" value={tourDuration} onChange={(e) => setTourDuration(e.target.value)} placeholder="e.g. 3 hours, half-day" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Cruise ship name <span className="text-slate-500">(optional)</span></label>
                    <input data-testid="input-cruise-ship" value={cruiseShip} onChange={(e) => setCruiseShip(e.target.value)} placeholder="Start typing or pick your ship" className={inputCls} list="cruise-ship-list" />
                    <datalist id="cruise-ship-list">
                      {[...new Set(cruiseList.map((c) => c.ship_name))].map((name) => {
                        const call = cruiseList.find((c) => c.ship_name === name);
                        return <option key={name} value={name}>{call?.arrival_date ? `Arrives ${call.arrival_date}` : ""}</option>;
                      })}
                    </datalist>
                  </div>
                  <div className="sm:col-span-2 rounded-sm border border-gold/20 bg-navy px-4 py-3 text-sm text-slate-400">
                    Already agreed a price with us by phone? Enter it in the <span className="text-gold">Agreed fare price</span> field below and book instantly.
                  </div>
                </>
              )}
              {(service === "city_tour" || service === "tours") && (
                <>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Choose your tour duration</label>
                    <Select value={option} onValueChange={setOption}>
                      <SelectTrigger data-testid="select-tour-package" className="bg-input border-gold/20 text-white h-12"><SelectValue placeholder="Select duration" /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(TOUR_PACKAGES).map(([k, p]) => (
                          <SelectItem key={k} value={k} data-testid={`tour-opt-${k}`}>{p.hours} hours — {p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {option && TOUR_PACKAGES[option] && (
                    <div className="sm:col-span-2 rounded-sm bg-navy border border-gold/25 p-4" data-testid="tour-checklist">
                      <div className="text-xs uppercase tracking-wider text-gold/80 mb-3">Included on your {TOUR_PACKAGES[option].hours}-hour tour</div>
                      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                        {TOUR_PACKAGES[option].stops.map((s) => (
                          <li key={s} className="flex items-center gap-2 text-sm text-slate-200" data-testid={`tour-stop-${s.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}>
                            <Check className="h-4 w-4 text-gold shrink-0" /> {s}
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3 pt-3 border-t border-gold/15 text-xs text-slate-400">
                        Optional extras available below: child booster seat, car seat, extra-large vehicle.
                      </div>
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Group size</label>
                    <Select value={passengers} onValueChange={setPassengers}>
                      <SelectTrigger data-testid="select-tour-pax" className="bg-input border-gold/20 text-white h-12"><SelectValue placeholder="Select group size" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">Up to 3 passengers — $65/hr{option && TOUR_PACKAGES[option] ? ` ($${TOUR_PACKAGES[option].hours * 65})` : ""}</SelectItem>
                        <SelectItem value="6">4–6 passengers — $75/hr{option && TOUR_PACKAGES[option] ? ` ($${TOUR_PACKAGES[option].hours * 75})` : ""}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              {service === "long_trip" && (
                <div className="sm:col-span-2">
                  <label className={labelCls}>Destination (to/from Halifax)</label>
                  <Select value={option} onValueChange={setOption}>
                    <SelectTrigger data-testid="select-long-trip" className="bg-input border-gold/20 text-white h-12"><SelectValue placeholder="Select destination" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {Object.entries(LONG_TRIP).map(([k, d]) => (
                        <SelectItem key={k} value={k} data-testid={`longtrip-opt-${k}`}>{d.label} — ${d.price}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {service === "local_area" && (
                <div className="sm:col-span-2">
                  <label className={labelCls}>Area (to/from Halifax Airport)</label>
                  <Select value={option} onValueChange={setOption}>
                    <SelectTrigger data-testid="select-local-area" className="bg-input border-gold/20 text-white h-12"><SelectValue placeholder="Select area" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {Object.entries(LOCAL).map(([k, d]) => (
                        <SelectItem key={k} value={k} data-testid={`local-opt-${k}`}>{d.label} — ${d.price}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {service === "shared_shuttle" && (
                <div className="sm:col-span-2">
                  <label className={labelCls}>Number of passengers</label>
                  <Select value={passengers} onValueChange={setPassengers}>
                    <SelectTrigger data-testid="select-passengers" className="bg-input border-gold/20 text-white h-12"><SelectValue placeholder="Select passengers" /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>{n} — ${SHARED_SHUTTLE_PER_PERSON.toFixed(2)}/person</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {service === "private_group_shuttle" && (
                <div className="sm:col-span-2">
                  <label className={labelCls}>Number of passengers (same pickup address)</label>
                  <Select value={passengers} onValueChange={setPassengers}>
                    <SelectTrigger data-testid="select-group-passengers" className="bg-input border-gold/20 text-white h-12"><SelectValue placeholder="Select passengers" /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>{n} passenger(s) — ${GROUP_SHUTTLE[n].toFixed(2)} each (${(GROUP_SHUTTLE[n] * n).toFixed(2)} total)</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {isHourly && (
                <div className="sm:col-span-2">
                  <label className={labelCls}>Group size</label>
                  <Select value={passengers} onValueChange={setPassengers}>
                    <SelectTrigger data-testid="select-valley-pax" className="bg-input border-gold/20 text-white h-12"><SelectValue placeholder="Select group size" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">Up to 3 passengers — $65/hr</SelectItem>
                      <SelectItem value="6">4–6 passengers — $75/hr</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isHourly && (
                <div className="sm:col-span-2">
                  <label className={labelCls}>Number of hours (min {HOURLY[service].min})</label>
                  <input data-testid="input-hours" type="number" min={HOURLY[service].min} value={hours} onChange={(e) => setHours(e.target.value)} placeholder={`e.g. ${HOURLY[service].min}`} className={inputCls} />
                </div>
              )}
              {service && service !== "custom_fare" && (
                <div className="sm:col-span-2">
                  <label className={labelCls}>Number of vehicles <span className="text-slate-500">(for larger groups / events)</span></label>
                  <Select value={vehicles} onValueChange={setVehicles}>
                    <SelectTrigger data-testid="select-vehicles" className="bg-input border-gold/20 text-white h-12"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => <SelectItem key={n} value={String(n)}>{n} vehicle{n > 1 ? "s" : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Trip details */}
          <div className="rounded-sm bg-navy-light border border-gold/20 p-7">
            <h3 className="font-heading text-lg font-bold text-white mb-5">2. Trip details</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Pickup date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="date-trigger" className={`${inputCls} flex items-center gap-2 text-left ${!date && "text-slate-500"}`}>
                      <CalIcon className="h-4 w-4 text-gold" />{date ? format(date, "PPP") : "Select date"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-navy-light border-gold/30" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className={labelCls}>{service === "tour" ? "Pickup time" : "Pickup time"}</label>
                <TimeSelect idPrefix="pickup-time" hour={timeH} minute={timeMin} ampm={timeAP}
                  onHour={setTimeH} onMinute={setTimeMin} onAmpm={setTimeAP} />
              </div>
              {service === "tour" && (
                <div>
                  <label className={labelCls}>Drop-off time <span className="text-slate-500">(optional)</span></label>
                  <TimeSelect idPrefix="dropoff-time" hour={dropH} minute={dropMin} ampm={dropAP}
                    onHour={setDropH} onMinute={setDropMin} onAmpm={setDropAP} />
                </div>
              )}
              <div className="sm:col-span-2">
                <label className={labelCls}>Pickup location</label>
                <input data-testid="input-pickup" value={form.pickup} onChange={setF("pickup")} placeholder="Address or airport terminal" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Drop-off location <span className="text-slate-500">(optional)</span></label>
                <input data-testid="input-dropoff" value={form.dropoff} onChange={setF("dropoff")} placeholder="Destination address" className={inputCls} />
              </div>

              <div className="sm:col-span-2">
                <label className={labelCls}>Additional stops <span className="text-slate-500">(optional)</span></label>
                <div className="space-y-2">
                  {stops.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input data-testid={`input-stop-${i}`} value={s}
                        onChange={(e) => setStops(stops.map((v, idx) => (idx === i ? e.target.value : v)))}
                        placeholder={`Stop ${i + 1} address`} className={inputCls} />
                      <button type="button" data-testid={`remove-stop-${i}`} onClick={() => setStops(stops.filter((_, idx) => idx !== i))}
                        className="shrink-0 grid place-items-center h-12 w-12 rounded-sm border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors duration-200">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {stops.length < 8 && (
                    <button type="button" data-testid="add-stop-btn" onClick={() => setStops([...stops, ""])}
                      className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-sm text-gold hover:bg-gold/10 transition-colors duration-200">
                      <Plus className="h-4 w-4" /> Add a stop
                    </button>
                  )}
                </div>
                <p className="mt-1.5 text-xs text-slate-500">Add extra pickup/drop-off points along the way. We'll confirm any fare change for multi-stop trips.</p>
              </div>

              <div className="sm:col-span-2">
                <label className={labelCls}>Additional fee <span className="text-slate-500">(optional)</span></label>
                <Select value={addonFee} onValueChange={(v) => setAddonFee(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="select-addon-fee" className="bg-input border-gold/20 text-white h-12"><SelectValue placeholder="No additional fee" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="none">No additional fee</SelectItem>
                    {ADDON_FEES.map((f) => <SelectItem key={f} value={String(f)} data-testid={`addon-fee-${f}`}>+ ${f}.00</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="mt-1.5 text-xs text-slate-500">Optional extra charge (e.g. multi-stop, waiting time or special requests). Added to your fare.</p>
              </div>

              {rtEligible && (
                <div className="sm:col-span-2">
                  <label className="flex items-center gap-3 rounded-sm border border-gold/25 bg-navy p-4 cursor-pointer hover:border-gold/50 transition-colors duration-200" data-testid="round-trip-toggle">
                    <input type="checkbox" checked={roundTrip} onChange={(e) => setRoundTrip(e.target.checked)} data-testid="round-trip-checkbox" className="h-5 w-5 accent-gold" />
                    <span>
                      <span className="block text-white font-semibold">Make it a round trip</span>
                      <span className="block text-xs text-slate-400">We'll bring you back too — fare is doubled to cover both directions.</span>
                    </span>
                  </label>
                </div>
              )}

              {rtEligible && roundTrip && (
                <>
                  <div>
                    <label className={labelCls}>Return date <span className="text-slate-500">(optional)</span></label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" data-testid="return-date-trigger" className={`${inputCls} flex items-center gap-2 text-left ${!returnDate && "text-slate-500"}`}>
                          <CalIcon className="h-4 w-4 text-gold" />{returnDate ? format(returnDate, "PPP") : "Select return date"}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-navy-light border-gold/30" align="start">
                        <Calendar mode="single" selected={returnDate} onSelect={setReturnDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <label className={labelCls}>Return time <span className="text-slate-500">(optional)</span></label>
                    <TimeSelect idPrefix="return-time" hour={rtH} minute={rtMin} ampm={rtAP}
                      onHour={setRtH} onMinute={setRtMin} onAmpm={setRtAP} />
                  </div>
                </>
              )}
              <div>
                <label className={labelCls}>Flight number <span className="text-slate-500">(optional)</span></label>
                <input data-testid="input-flight" value={form.flight_number} onChange={setF("flight_number")} placeholder="e.g. AC1234" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>How much luggage?</label>
                <Select value={luggage} onValueChange={setLuggage}>
                  <SelectTrigger data-testid="select-luggage" className="bg-input border-gold/20 text-white h-12"><SelectValue placeholder="Select luggage" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No luggage</SelectItem>
                    <SelectItem value="1">1 bag</SelectItem>
                    <SelectItem value="2">2 bags</SelectItem>
                    <SelectItem value="3">3 bags</SelectItem>
                    <SelectItem value="4+">4 or more bags</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Are you checking bags?</label>
                <Select value={checkingBags} onValueChange={setCheckingBags}>
                  <SelectTrigger data-testid="select-checking-bags" className="bg-input border-gold/20 text-white h-12"><SelectValue placeholder="Select an option" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes" data-testid="checking-bags-yes">Yes, checking bags</SelectItem>
                    <SelectItem value="no" data-testid="checking-bags-no">No, carry-on only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Add-ons <span className="text-slate-500">(optional, $10 each)</span></label>
                <div className="grid gap-3 sm:grid-cols-3">
                  {EXTRAS.map((ex) => (
                    <button type="button" key={ex.value} onClick={() => toggleExtra(ex.value)} data-testid={`extra-${ex.value}`}
                      className={`flex items-center justify-between gap-2 rounded-sm border px-4 py-3 text-left transition-colors duration-200 ${extras.includes(ex.value) ? "border-gold bg-gold/10 text-white" : "border-gold/20 text-slate-300 hover:border-gold/50"}`}>
                      <span className="text-sm font-medium">{ex.label}</span>
                      <span className={`text-sm font-semibold ${extras.includes(ex.value) ? "text-gold" : "text-slate-400"}`}>+${ex.price}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Notes <span className="text-slate-500">(optional)</span></label>
                <textarea data-testid="input-notes" rows={3} value={form.notes} onChange={setF("notes")} placeholder="Flight number, luggage, child seat…" className={`${inputCls} resize-none`} />
              </div>
              {service && service !== "custom_fare" && (
                <div className="sm:col-span-2 rounded-sm border border-gold/25 bg-navy p-4">
                  <label className={labelCls}>Agreed fare price <span className="text-slate-500">(optional)</span></label>
                  <div className="relative max-w-[220px]">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gold">$</span>
                    <input data-testid="input-custom-fare" type="number" min="0" step="0.01" value={customFare}
                      onChange={(e) => setCustomFare(e.target.value)} placeholder="Enter agreed price"
                      className={`${inputCls} pl-7`} />
                  </div>
                  <p className="mt-2 text-xs text-slate-500">Have a price you've agreed with us? Enter it here and it replaces the calculated fare. Leave blank to use the fare above.</p>
                </div>
              )}
            </div>
          </div>

          <button type="button" onClick={addToCart} data-testid="add-service-btn"
            className="w-full flex items-center justify-center gap-2 rounded-sm border-2 border-dashed border-gold/40 py-4 text-gold font-semibold hover:bg-gold/10 transition-colors duration-200">
            <Plus className="h-5 w-5" /> Add another service to your order
          </button>

          {/* Contact */}
          <div className="rounded-sm bg-navy-light border border-gold/20 p-7">
            <h3 className="font-heading text-lg font-bold text-white mb-5">3. Your details <span className="text-slate-500 text-sm font-normal">(all optional)</span></h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <input data-testid="input-name" value={form.name} onChange={setF("name")} placeholder="Full name (optional)" className={inputCls} />
              <input data-testid="input-phone" value={form.phone} onChange={setF("phone")} placeholder="Phone number (optional)" className={inputCls} />
              <input data-testid="input-email" type="email" value={form.email} onChange={setF("email")} placeholder="Email address (optional)" className={`${inputCls} sm:col-span-2`} />
            </div>
            <p className="mt-3 text-xs text-slate-500">Add your phone or email if you'd like a confirmation and updates — otherwise we'll confirm your ride directly.</p>
          </div>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="sticky top-28 rounded-sm bg-navy-light border border-gold/30 p-7">
            <h3 className="font-heading text-lg font-bold text-white mb-5">Fare summary</h3>
            {cart.length > 0 && (
              <div className="mb-4 space-y-2" data-testid="cart-list">
                <div className="text-xs uppercase tracking-wider text-gold/80">Your order</div>
                {cart.map((it, i) => (
                  <div key={i} className="flex items-start justify-between gap-2 rounded-sm bg-navy border border-gold/15 p-3" data-testid={`cart-item-${i}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{it.description}</div>
                      <div className="text-xs text-slate-500">{it.date} at {it.time}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-semibold text-gold">${it.amount.toFixed(2)}</span>
                      <button type="button" onClick={() => removeItem(i)} data-testid={`cart-remove-${i}`} className="text-slate-500 hover:text-red-400 transition-colors"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {orderTotal > 0 ? (
              <div className="space-y-3">
                {quote && <p className="text-slate-400 text-sm">{cart.length > 0 ? "Adding now: " : ""}{quote.desc}</p>}
                {(cart.length > 0 || discount > 0 || taxAmount > 0) && (
                  <div className="flex justify-between text-sm"><span className="text-slate-400">Subtotal</span><span className="text-slate-200" data-testid="order-subtotal">${orderTotal.toFixed(2)}</span></div>
                )}
                {discount > 0 && (
                  <div className="flex justify-between text-sm"><span className="text-emerald-300">Promo ({promoInfo.percent}% off)</span><span className="text-emerald-300" data-testid="order-discount">−${discount.toFixed(2)}</span></div>
                )}
                {taxAmount > 0 && (
                  <div className="flex justify-between text-sm"><span className="text-slate-400">HST 15%</span><span className="text-slate-200" data-testid="order-tax">+${taxAmount.toFixed(2)}</span></div>
                )}
                <div className="flex items-end justify-between border-t border-gold/20 pt-4">
                  <span className="text-slate-300">Total</span>
                  <span className="font-heading text-4xl font-bold text-gold" data-testid="fare-total">${total.toFixed(2)}</span>
                </div>
                {showPlan && plan === "deposit" && (
                  <div className="rounded-sm bg-navy border border-gold/20 p-4 space-y-2" data-testid="fare-breakdown">
                    <div className="flex justify-between text-sm"><span className="text-slate-400">Due now (deposit)</span><span className="font-semibold text-white" data-testid="fare-due-now">${dueNow.toFixed(2)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-400">Balance on arrival</span><span className="font-semibold text-white" data-testid="fare-balance">${balanceAfter.toFixed(2)}</span></div>
                  </div>
                )}
                <p className="text-xs text-slate-500">{payment === "online" ? "CAD · HST 15% added for online card payments" : "CAD · HST added only if you pay online by card"}</p>
              </div>
            ) : (
              <p className="text-slate-500 text-sm">Select a service to see your fare.</p>
            )}

            <div className="mt-5" data-testid="promo-section">
              <label className={labelCls}>Promo / loyalty code <span className="text-slate-500">(optional)</span></label>
              <div className="flex items-center gap-2">
                <input value={promo} onChange={(e) => { setPromo(e.target.value); setPromoInfo(null); }} placeholder="Enter code" data-testid="promo-input" className={`${inputCls} uppercase`} />
                <button type="button" onClick={applyPromo} disabled={promoLoading} data-testid="promo-apply-btn" className="shrink-0 rounded-full border border-gold/40 px-4 py-3 text-sm font-semibold text-gold hover:bg-gold/10 disabled:opacity-60 transition-colors duration-200">
                  {promoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                </button>
              </div>
              {promoInfo && <p className="mt-2 text-xs text-emerald-300" data-testid="promo-applied"><Tag className="inline h-3 w-3 mr-1" />{promoInfo.percent}% off applied</p>}
            </div>

            <div className="mt-6 space-y-3">
              <label className={labelCls}>Payment method</label>
              {[
                { v: "online", i: CreditCard, t: "Pay online now (card / wallet)" },
                { v: "e_transfer", i: Send, t: "Interac e-Transfer" },
                { v: "pay_later", i: Wallet, t: "Pay driver on arrival (cash or debit machine)" },
              ].map((p) => (
                <button type="button" key={p.v} onClick={() => setPayment(p.v)} data-testid={`pay-${p.v}`}
                  className={`w-full flex items-center gap-3 rounded-sm border px-4 py-3 text-left transition-colors duration-200 ${payment === p.v ? "border-gold bg-gold/10 text-white" : "border-gold/20 text-slate-300 hover:border-gold/50"}`}>
                  <p.i className={`h-5 w-5 shrink-0 ${payment === p.v ? "text-gold" : "text-slate-400"}`} />
                  <span className="text-sm font-medium">{p.t}</span>
                </button>
              ))}
            </div>

            {showPlan && (
              <div className="mt-6 space-y-3" data-testid="payment-plan-section">
                <label className={labelCls}>Payment amount</label>
                {[
                  { v: "full", t: "Pay full amount", s: `$${total.toFixed(2)} now` },
                  { v: "deposit", t: "Pay deposit now", s: `$${deposit.toFixed(2)} now · $${(total - deposit).toFixed(2)} on arrival` },
                ].map((p) => (
                  <button type="button" key={p.v} onClick={() => setPlan(p.v)} data-testid={`plan-${p.v}`}
                    className={`w-full flex items-center justify-between gap-3 rounded-sm border px-4 py-3 text-left transition-colors duration-200 ${plan === p.v ? "border-gold bg-gold/10 text-white" : "border-gold/20 text-slate-300 hover:border-gold/50"}`}>
                    <span className="text-sm font-medium">{p.t}</span>
                    <span className={`text-xs ${plan === p.v ? "text-gold" : "text-slate-400"}`}>{p.s}</span>
                  </button>
                ))}
              </div>
            )}

            {payment === "online" && total > 0 && (
              <div className="mt-6 space-y-3" data-testid="tip-section">
                <label className={labelCls}>Add a tip for your driver <span className="text-slate-500">(optional)</span></label>
                <div className="grid grid-cols-4 gap-2">
                  {[0, 10, 15, 20].map((p) => (
                    <button type="button" key={p} onClick={() => { setTipPct(p); setCustomTip(""); }} data-testid={`tip-${p}`}
                      className={`rounded-sm border px-2 py-2.5 text-sm font-medium transition-colors duration-200 ${customTip === "" && tipPct === p ? "border-gold bg-gold/10 text-gold" : "border-gold/20 text-slate-300 hover:border-gold/50"}`}>
                      {p === 0 ? "None" : `${p}%`}
                    </button>
                  ))}
                </div>
                <input type="number" min="0" step="0.5" value={customTip} onChange={(e) => setCustomTip(e.target.value)} placeholder="Or custom amount ($)" data-testid="tip-custom" className={inputCls} />
                {tipAmount > 0 && <div className="flex justify-between text-sm"><span className="text-slate-400">Tip</span><span className="font-semibold text-gold" data-testid="tip-amount">${tipAmount.toFixed(2)}</span></div>}
                <div className="flex justify-between border-t border-gold/20 pt-3"><span className="text-slate-300 font-medium">Charged now</span><span className="font-heading text-xl font-bold text-gold" data-testid="charged-now">${chargedNow.toFixed(2)}</span></div>
              </div>
            )}

            <label className="mt-6 flex items-start gap-3 cursor-pointer group" data-testid="terms-label">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="terms-checkbox"
                className="mt-0.5 h-5 w-5 shrink-0 rounded-sm border border-gold/40 bg-input accent-gold cursor-pointer" />
              <span className="text-xs text-slate-400 leading-relaxed">
                I agree to the <button type="button" onClick={(e) => { e.preventDefault(); setShowTerms(true); }} data-testid="terms-open" className="text-gold underline underline-offset-2 hover:text-gold-light">Terms &amp; Conditions</button> and cancellation policy.
              </span>
            </label>

            <button type="submit" disabled={loading} data-testid="booking-submit"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 font-semibold text-navy transition-transform duration-200 hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-60">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing…</> : payment === "online" ? (plan === "deposit" ? "Pay Deposit & Confirm" : "Pay & Confirm") : "Confirm Booking"}
            </button>
          </div>
        </div>
      </form>

      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="bg-navy-light border-gold/30 text-slate-300 max-w-lg max-h-[80vh] overflow-y-auto" data-testid="terms-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-white">Terms &amp; Conditions</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm leading-relaxed mt-2">
            <p><span className="text-gold font-semibold">1. Booking &amp; Confirmation.</span> All bookings are requests until confirmed by Bridge Taxi, Airport Shuttle &amp; Tours. Quoted fares are in CAD and include applicable taxes.</p>
            <p><span className="text-gold font-semibold">2. Cancellations.</span> Cancellations made at least 2 hours before pickup are free of charge. Late cancellations or no-shows may be charged the full fare.</p>
            <p><span className="text-gold font-semibold">3. Payment.</span> You may pay online in advance or pay the driver on arrival. Online payments are processed securely via Stripe.</p>
            <p><span className="text-gold font-semibold">4. Luggage.</span> Please declare luggage accurately. Excess or oversized bags may require a larger vehicle and could affect availability or fare.</p>
            <p><span className="text-gold font-semibold">5. Delays.</span> We monitor flights where a flight number is provided, but we are not liable for delays caused by traffic, weather, or circumstances beyond our control.</p>
            <p><span className="text-gold font-semibold">6. Conduct.</span> Passengers must behave respectfully. Drivers reserve the right to end a trip for unsafe or abusive behaviour.</p>
          </div>
          <button onClick={() => { setAgreed(true); setShowTerms(false); }} data-testid="terms-accept-btn"
            className="mt-4 w-full rounded-full bg-gold px-6 py-3 font-semibold text-navy transition-colors duration-200 hover:bg-gold-light">
            I Agree
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ l, v, gold }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-400">{l}</span>
      <span className={`text-right font-medium ${gold ? "text-gold" : "text-white"}`}>{v}</span>
    </div>
  );
}
